import {Normalizer, SnapshotEntity} from "../types";

// 'takbis' normalizeri — BorcluTakbisSorgulamaCevap (uyap/BorcluTakbisSorgulama.ts)
// girdisini SnapshotEntity[]'e indirger. Zincir katlanmis girdi: 'hisse' ve
// 'takyidat' secimleri de bu TEK pakete gomulur (DosyaBorclu.ts'teki
// queryTasinmazHisse / queryTasinmazTakyidat metotlari — kod inceleme: kesin
// satir numarasi degil metot adi verildi, DosyaBorclu.ts sonradan degistiginde
// bayatlamasin, T2-35), ayri paket URETILMEZ.
//
// Gercek sekil (parse() BorcluTakbisSorgulama.ts:102-121):
//   cevap.sorguSonucDVO: Array<IBorcluTakbisSorgulamaSonuc> | [] | undefined
//     - dizi ise: her elemana parse() selected:true ekler (ham UYAP JSON'unda yok).
//     - kurum hata metniyse (string): parse EXPLICIT olarak [] yapar.
//     - ikisi de degilse (orn. undefined): DOKUNULMAZ, undefined olarak KALIR.
//   -> "her zaman dizi" varsayimi YANLIS, Array.isArray ile savunulur.
//
//   Dizi elemani: {tasinmazDVO, hisse?, yuzolcumu(string kopya, KULLANILMAZ), selected}
//   tasinmazDVO: {tasinmazId, il, ilce, ada, parsel, nitelik, tapuBolumDurum,
//                 yuzolcum(number, kanonik), ...}
//
//   Zincir calistiysa (DosyaBorclu.ts queryTasinmazHisse icindeki
//   "tasinmaz.hisse = ..." atamasi):
//     tasinmaz.hisse = IBorcluTasinmazHisseSorgulamaSonuc SARMALAYICISIZ sekliyle
//       -> {hisseList: HisseList[], borcluBilgileri, sonuc, id, isCompressed,
//           htmlHataKodList, isMock} (ISorguSonuc alanlari YOK, tip tanimina guvenme)
//     Basarisiz denemede tasinmaz.hisse HIC ATANMAMIS (undefined) kalir — DosyaBorclu.ts
//     queryTasinmazHisse'nin hata dalindaki "tasinmaz.hisse.message = e.message"
//     undefined uzerinde calisirsa kendisi throw eder ve dongu erken kesilir; yani
//     {message} seklinde yarim bir tasinmaz.hisse GERCEK KOŞUMDA URETILMEZ (savunma
//     notu) ama yine de defansif okunur.
//
//     Her HisseList ogesine DosyaBorclu.ts queryTasinmazTakyidat icindeki
//     "hisse.takyidat = ..." eklenir:
//     hisse.takyidat = BorcluTasinmazTakyidatSorgulamaCevap
//       -> {serhList: SerhList[], sonuc, id, isCompressed, htmlHataKodList, isMock, message}
//     Ayni sekilde basarisiz denemede hisse.takyidat da undefined kalabilir/yarim
//     olabilir — Array.isArray + "?." ile savunulur, throw etmez.
//
// Anahtar: tasinmazDVO.tasinmazId. TEKILLIK GARANTI DEGIL (gercek UYAP mock'unda ayni
// tasinmazId iki kez gecebiliyor) — kanonikHash dedupe ETMEZ, bu normalizer de etmez;
// yalnizca ozet.tasinmazSayisi DISTINCT sayilir (ham dizi uzunlugu degil).
//
// yuzolcum: tasinmazDVO.yuzolcum (sayisal, kanonik) kullanilir; ust seviyedeki
// sorguSonucDVO[i].yuzolcumu (ayni degerin STRING kopyasi) KULLANILMAZ.
export const takbisNormalizer: Normalizer = (cevap) => {
    const bos = {entities: []};

    try {
        if (!cevap || typeof cevap !== "object") {
            return bos;
        }

        const tasinmazlar = cevap.sorguSonucDVO;
        if (!Array.isArray(tasinmazlar) || tasinmazlar.length === 0) {
            return bos;
        }

        const entities: SnapshotEntity[] = [];

        for (const tasinmaz of tasinmazlar) {
            if (!tasinmaz || typeof tasinmaz !== "object") {
                continue;
            }

            const tasinmazDVO = tasinmaz.tasinmazDVO;
            if (!tasinmazDVO || typeof tasinmazDVO !== "object") {
                continue;
            }

            const tasinmazId: unknown = tasinmazDVO.tasinmazId;
            // tasinmazId entity key'idir; olmayan/turu belirsiz kayit atlanir.
            if (tasinmazId === undefined || tasinmazId === null) {
                continue;
            }
            const key = String(tasinmazId);

            const il: unknown = tasinmazDVO.il;
            const ilce: unknown = tasinmazDVO.ilce;
            const ada: unknown = tasinmazDVO.ada;
            const parsel: unknown = tasinmazDVO.parsel;
            const nitelik: unknown = tasinmazDVO.nitelik;
            const tapuBolumDurum: unknown = tasinmazDVO.tapuBolumDurum;
            const yuzolcum: unknown = tasinmazDVO.yuzolcum;

            const attrs: Record<string, unknown> = {};
            if (typeof il === "string" && il.length > 0) attrs.il = il;
            if (typeof ilce === "string" && ilce.length > 0) attrs.ilce = ilce;
            if (typeof ada === "string" && ada.length > 0) attrs.ada = ada;
            if (typeof parsel === "string" && parsel.length > 0) attrs.parsel = parsel;
            if (typeof nitelik === "string" && nitelik.length > 0) attrs.nitelik = nitelik;
            if (typeof tapuBolumDurum === "string" && tapuBolumDurum.length > 0) {
                attrs.tapuBolumDurum = tapuBolumDurum;
            }
            if (typeof yuzolcum === "number" && !Number.isNaN(yuzolcum)) {
                attrs.yuzolcum = yuzolcum;
            }

            // hisse zinciri: yalnizca queryTasinmazHisse calistiysa var. hisseList array
            // degilse (hic sorgulanmadi / yarim {message} nesnesi) hisseler key'i hic
            // eklenmez — "sorgulanmadi" ile "sorgulandi, 0 hisse" ayrimi korunur.
            const hisseList = tasinmaz.hisse?.hisseList;
            if (Array.isArray(hisseList)) {
                const hisseler: Record<string, unknown>[] = [];
                for (const hisse of hisseList) {
                    if (!hisse || typeof hisse !== "object") {
                        continue;
                    }

                    const hisseAttrs: Record<string, unknown> = {};
                    if (hisse.hisseId !== undefined) hisseAttrs.hisseId = hisse.hisseId;
                    if (hisse.pay !== undefined) hisseAttrs.pay = hisse.pay;
                    if (hisse.payda !== undefined) hisseAttrs.payda = hisse.payda;

                    // takyidat zinciri: yalnizca queryTasinmazTakyidat calistiysa var.
                    // Tek bir takyidat cumlesi degisirse hash degismeli (K5) — cumleler
                    // TAM (kisaltmadan) attrs'a girer.
                    const serhList = hisse.takyidat?.serhList;
                    if (Array.isArray(serhList)) {
                        const takyidatlar: Record<string, unknown>[] = [];
                        for (const serh of serhList) {
                            if (!serh || typeof serh !== "object") {
                                continue;
                            }
                            const t: Record<string, unknown> = {};
                            if (serh.sablon !== undefined) t.sablon = serh.sablon;
                            if (serh.cumle !== undefined) t.cumle = serh.cumle;
                            takyidatlar.push(t);
                        }
                        hisseAttrs.takyidatlar = takyidatlar;
                    }

                    hisseler.push(hisseAttrs);
                }
                attrs.hisseler = hisseler;
            }

            const yerParcalari = [il, ilce].filter(
                (parca): parca is string => typeof parca === "string" && parca.length > 0
            );
            const adaParsel = [ada, parsel]
                .filter((parca): parca is string => typeof parca === "string" && parca.length > 0)
                .join("/");
            let label = yerParcalari.join(" ");
            if (adaParsel) label += label ? ` (${adaParsel})` : adaParsel;
            if (typeof nitelik === "string" && nitelik.length > 0) {
                label += label ? ` - ${nitelik}` : nitelik;
            }
            if (!label) label = key;

            entities.push({key, label, attrs});
        }

        // ozet.tasinmazSayisi DISTINCT tasinmazId sayisidir — ham dizi uzunlugu
        // (tasinmazlar.length) KULLANILMAZ, ayni tasinmazId birden fazla kez gecebilir.
        const distinctKeySayisi = new Set(entities.map((e) => e.key)).size;

        return {
            entities,
            ozet: {tasinmazSayisi: distinctKeySayisi}
        };
    } catch {
        // ASLA THROW ETME: beklenmeyen sekil gelirse bos entities don.
        return bos;
    }
};
